import React from 'react';
import { motion } from 'framer-motion';
import { tokenomicsConfig } from '@/data/tokenomicsData';
import { Button } from '@/components/ui/button';
import { 
  Eye, Vote, BarChart3, Ban, Clock, 
  Shield, ExternalLink, Wallet 
} from 'lucide-react';

const iconMap = {
  'Public Wallets': Wallet,
  'DAO Governance': Vote,
  'Treasury Dashboard': BarChart3,
  'No Discretionary Minting': Ban,
  'Predictable Emissions': Clock,
  'Multisig Controls': Shield,
};

export const TransparencySection = () => {
  return (
    <section id="transparency" className="py-24 relative">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-accent/5 to-background" />
      <div className="absolute top-0 left-0 right-0 h-px neural-line" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-primary text-sm font-medium tracking-wider uppercase">
            Transparency
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-foreground mt-3 mb-4">
            DAO & Transparency
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Complete transparency through on-chain verification, DAO governance, 
            and publicly viewable foundation wallets.
          </p>
        </motion.div>
        
        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {tokenomicsConfig.daoFeatures.map((feature, index) => {
            const Icon = iconMap[feature.title] || Eye;
            
            return (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-xl p-6 group hover:border-accent/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                  <Icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>
        
        {/* Coming Soon Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8"
        >
          <h3 className="font-heading text-xl font-semibold text-foreground mb-6 text-center">
            Transparency Tools (Coming Soon)
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-border rounded-xl p-4 opacity-60 hover:opacity-80 transition-opacity">
              <Wallet className="w-6 h-6 text-muted-foreground mb-3" />
              <h4 className="font-medium text-foreground mb-1">Foundation Wallet Explorer</h4>
              <p className="text-xs text-muted-foreground">View all foundation wallets on-chain</p>
            </div>
            
            <div className="border border-border rounded-xl p-4 opacity-60 hover:opacity-80 transition-opacity">
              <Vote className="w-6 h-6 text-muted-foreground mb-3" />
              <h4 className="font-medium text-foreground mb-1">Governance Portal</h4>
              <p className="text-xs text-muted-foreground">Participate in DAO proposals</p>
            </div>
            
            <div className="border border-border rounded-xl p-4 opacity-60 hover:opacity-80 transition-opacity">
              <BarChart3 className="w-6 h-6 text-muted-foreground mb-3" />
              <h4 className="font-medium text-foreground mb-1">Snapshot Proposals</h4>
              <p className="text-xs text-muted-foreground">View and vote on proposals</p>
            </div>
            
            <div className="border border-border rounded-xl p-4 opacity-60 hover:opacity-80 transition-opacity">
              <Clock className="w-6 h-6 text-muted-foreground mb-3" />
              <h4 className="font-medium text-foreground mb-1">Emissions Schedule</h4>
              <p className="text-xs text-muted-foreground">Track predicted unlock schedule</p>
            </div>
          </div>
        </motion.div>
        
        {/* Neural line separator */}
        <div className="mt-24 neural-line" />
      </div>
    </section>
  );
};

export default TransparencySection;
